import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  DefaultPaginationValues,
  createSelectionForAdminOptionsUtil,
  createSelectionOptionsUtil,
  queryString,
} from '../../../Util/commonUtility';
import {
  deleteEventsById,
  duplicateEventsById,
  editEventStatusById,
  getAllEventsByQueryString,
} from './../../../Store/Actions/events';
import EventsComponent from './../../../Components/AdminComponents/Events/index';
import {useNavigate} from 'react-router-dom';
import {
  getAllChapters,
  getAllChaptersByQueryString,
  getAllChaptersByQueryStringForSearch,
  getAllEventTypes,
  getAllEventTypesByQuery,
  getAllEventTypesByQuerySearch,
  getAllSpeakers,
  getAllSpeakersByQuerySearch,
} from '../../../Store/Actions';
import {setPageNumber, setPageSize} from '../../../Store/Actions/pageSize';
import dayjs from 'dayjs';

const EVENTS_FILTER_STORAGE_KEY = 'eventsAdminTimeFilter';

const readStoredEventFilter = () => {
  try {
    const saved = sessionStorage.getItem(EVENTS_FILTER_STORAGE_KEY);
    return saved === 'past' ? 'past' : 'upcoming';
  } catch (error) {
    return 'upcoming';
  }
};

const storeEventFilter = (filter) => {
  try {
    sessionStorage.setItem(EVENTS_FILTER_STORAGE_KEY, filter);
  } catch (error) {
    // Ignore storage failures (for example private mode).
  }
};

const sortForEventFilter = (filter) => ({
  field: 'event_start_date_time',
  order: filter === 'past' ? 'descend' : 'ascend',
});

const EventsContainer = () => {
  const EVENTS_DEFAULT_PAGE_SIZE = 25;
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const {allEventsQueryData} = useSelector(({events}) => events);
  const {allEventTypesQueryData} = useSelector(({eventTypes}) => eventTypes);
  const {allChaptersQueryData} = useSelector(({chapters}) => chapters);
  const {allSpeakersQueryData} = useSelector(({speakers}) => speakers);
  const memorizedPageNumber = useSelector(
    ({pageSizes}) => pageSizes.pageNumbers.events,
  );
  const memorizedPageSize = useSelector(
    ({pageSizes}) => pageSizes.pageSizes.events,
  );

  const [eventsData, setEventsData] = useState();
  const [pagination, setPagination] = useState({
    page: memorizedPageNumber || DefaultPaginationValues.PAGE,
    pageCount: memorizedPageSize || EVENTS_DEFAULT_PAGE_SIZE,
    totalPages: DefaultPaginationValues.TOTAL_PAGES,
  });
  const initialEventFilter = readStoredEventFilter();
  const [sortParams, setSortParams] = useState(() =>
    sortForEventFilter(initialEventFilter),
  );
  const [chaptersOptions, setChaptersOptions] = useState([]);
  const [speakerOptions, setSpeakerOptions] = useState([]);
  const [eventTypesOptions, SetEventTypesOption] = useState([]);
  const [paginationAndSortChanged, setPaginationAndSortChanged] =
    useState(false);
  const searchValues = useSelector(
    ({dynamicSearch}) => dynamicSearch.searchValues.events,
  );
  const [fetching, setFetching] = useState(true);
  const [eventFilter, setEventFilter] = useState(initialEventFilter);
  // Filter the in-flight list request belongs to. Stops a tab change from
  // requesting the same list again while that request is still running.
  const requestedFilterRef = useRef(null);

  useEffect(() => {
    if (allSpeakersQueryData.forSearch) {
      setSpeakerOptions(
        createSelectionForAdminOptionsUtil(
          allSpeakersQueryData.forSearch?.data?.speakers,
          'id',
          'first_name',
          'last_name',
        ),
      );
      setFetching(false);
    } else {
      dispatch(
        getAllSpeakersByQuerySearch(
          {
            page: 1,
            pagecount: 10,
          },
          '',
          '',
          '',
        ),
      );
    }
  }, [allSpeakersQueryData]);

  useEffect(() => {
    if (searchValues) {
      dispatch(setPageNumber('events', 1));
      setPagination({
        ...pagination,
        page: 1,
      });
      setPaginationAndSortChanged(true);
    }
  }, [searchValues]);

  useEffect(() => {
    if (allEventTypesQueryData.forSearch) {
      const eventTypesOptions = createSelectionOptionsUtil(
        allEventTypesQueryData.forSearch?.data?.eventTypes,
        'id',
        'name',
      );
      SetEventTypesOption(eventTypesOptions);
      setFetching(false);
    } else {
      dispatch(
        getAllEventTypesByQuerySearch('', '', '', {
          page: 1,
          pageCount: 100,
        }),
      );
    }
  }, [allEventTypesQueryData.forSearch?.data?.eventTypes]);

  const formatSearchValues = (values) => {
    if (!values) return {};
    return {
      ...values,
      date:
        values?.date?.length > 0
          ? [
              dayjs(values.date[0]).format('YYYY-MM-DD'),
              dayjs(values.date[1]).format('YYYY-MM-DD'),
            ]
          : '',
    };
  };

  const fetchEvents = (filterValues, overrides = {}) => {
    const filter = overrides.filter ?? eventFilter;
    const sortField = overrides.sortField ?? sortParams.field ?? '';
    const sortOrder = overrides.sortOrder ?? sortParams.order ?? '';
    const pageState = overrides.pagination ?? pagination;
    const searchQueryData = queryString({
      ...(filterValues || {}),
      filter,
    });
    dispatch(
      getAllEventsByQueryString(
        pageState,
        sortField,
        sortOrder,
        searchQueryData,
        undefined,
        filter,
      ),
    );
  };

  useEffect(() => {
    const master = allEventsQueryData?.forMaster;
    const cachedFilter = allEventsQueryData?.forMasterFilter;
    // Only show rows that were loaded for the tab currently selected.
    // Coming back from a registration list used to reuse the past-events
    // response while the Upcoming tab was selected again.
    if (master && cachedFilter === eventFilter) {
      requestedFilterRef.current = null;
      setEventsData(master?.data?.events);
      setPagination((prev) => ({
        ...prev,
        page: master?.data?.currentPage,
        totalPages: master?.data?.totalPage,
        totalCount: master?.data?.totalCount,
      }));
      return;
    }

    setEventsData(undefined);
    if (requestedFilterRef.current === eventFilter) {
      return;
    }
    requestedFilterRef.current = eventFilter;
    fetchEvents(formatSearchValues(searchValues));
  }, [
    allEventsQueryData?.forMaster?.data?.events,
    allEventsQueryData?.forMasterFilter,
    eventFilter,
  ]);

  useEffect(() => {
    if (paginationAndSortChanged) {
      fetchEvents(formatSearchValues(searchValues));
      setPaginationAndSortChanged(false);
    }
  }, [paginationAndSortChanged, searchValues]);

  useEffect(() => {
    if (allChaptersQueryData?.forSearch) {
      const visibleChapters =
        allChaptersQueryData.forSearch?.data?.chapters?.filter(
          (chapter) => chapter?.is_visible_on_map === true,
        );
      setChaptersOptions(
        createSelectionOptionsUtil(visibleChapters, 'id', 'name'),
      );
    } else {
      dispatch(
        getAllChaptersByQueryStringForSearch('', '', '', {
          page: 1,
          pageCount: 100,
        }),
      );
    }
  }, [allChaptersQueryData?.forSearch?.data?.chapters]);

  const handlePaginationChange = (current, pageSize) => {
    dispatch(setPageSize('events', pageSize));
    dispatch(setPageNumber('events', current));
    setPagination({
      ...pagination,
      page: current,
      pageCount: pageSize,
    });
    setPaginationAndSortChanged(true);
  };

  const handleTableChange = (pagination, filter, sorter) => {
    setSortParams({
      field: sorter.field,
      order: sorter.order,
    });
    setPaginationAndSortChanged(true);
  };

  const updateStatusById = async (eventData, status) => {
    const id = eventData.id;
    dispatch(editEventStatusById(id, status));
  };
  const handleFilterChange = (value) => {
    const nextSort = sortForEventFilter(value);
    const nextPagination = {
      ...pagination,
      page: 1,
    };
    storeEventFilter(value);
    setSortParams(nextSort);
    dispatch(setPageNumber('events', 1));
    setPagination(nextPagination);
    setEventsData(undefined);

    if (value === eventFilter) {
      // Already on this tab: onChange does not run, so reload it directly.
      requestedFilterRef.current = value;
      fetchEvents(formatSearchValues(searchValues), {
        filter: value,
        sortField: nextSort.field,
        sortOrder: nextSort.order,
        pagination: nextPagination,
      });
      return;
    }

    requestedFilterRef.current = null;
    setEventFilter(value);
  };

  const deleteEvents = (id) => {
    dispatch(deleteEventsById(id));
  };
  const duplicateEvents = async (id) => {
    dispatch(duplicateEventsById(id, navigate));
  };
  return (
    <div className='headmain-form-container'>
      <EventsComponent
        speakerOptions={speakerOptions}
        setSpeakerOptions={setSpeakerOptions}
        fetching={fetching}
        setFetching={setFetching}
        allEventsData={eventsData}
        paging={pagination}
        eventTypesOptions={eventTypesOptions}
        SetEventTypesOption={SetEventTypesOption}
        chaptersOptions={chaptersOptions}
        handlePaginationChange={(page, pageSize) => {
          handlePaginationChange(page, pageSize);
        }}
        handleTableChange={handleTableChange}
        sortParams={sortParams}
        activeFilter={eventFilter}
        onFilterChange={handleFilterChange}
        fetchEvents={(searchValues) => {
          fetchEvents(searchValues);
        }}
        deleteEvents={(id) => {
          deleteEvents(id);
        }}
        duplicateEvents={(id) => {
          duplicateEvents(id);
        }}
        updateStatusById={(eventData, status) => {
          updateStatusById(eventData, status);
        }}
        searchValues={searchValues}
      />
    </div>
  );
};

export default EventsContainer;
